import React, { useMemo, useState } from 'react'
import { compose } from 'redux'
import PropTypes from 'prop-types'

import { apr } from '@cred/shared/formatters'

import withApolloProvider from 'components/hocs/withApolloProvider'
import WidgetPage from 'components/hocs/widget-page'
import { AdvertiserDisclosure } from 'components/advertiser-disclosure'
import { getMetaQuery } from 'utils/data'
import PoweredBy from 'components/powered-by'
import HeaderCTA from 'components/header-CTA'
import useActiveLenders from 'components/hooks/useActiveLenders'
import Superscript from 'components/RateTableFooterDisclosure/Superscript'
import RateTableFooterDisclosure from 'components/RateTableFooterDisclosure/Disclosure'
import { ADVERTISER_DISCLOSURE_THEMES } from 'common/constants'
import { getCtaUrl } from 'student-loan/shared/utils'
import withRateScript from 'components/hocs/with-rate-script'
import { loadLendersRates } from 'common/utils/rates'
import FooterLinks from 'components/footer-links'

import FullRateTableApp from './full-table'
import ShortenedRateTableApp from './shortened-table'
import styles from './styles.module.scss'

const ORDERED_RATE_TABLE_VARIATION = 'compare-rates-ordered'
const SHORTENED_RATE_TABLE_VARIATION = 'shortened'

const RateTableApp = (props) => {
  const availableLenders = useActiveLenders('in_school')
  const [isDisclosureOpen, setIsDisclosureOpen] = useState(false)

  const { theme, variation, metaParams, verticals, type, utms, isScriptLoaded } = props

  const minRate = useMemo(() => {
    if (isScriptLoaded) {
      const filters = { vertical: 'inschool', variable: true }
      const lendersWithRates = loadLendersRates({
        availableLenders,
        filters,
      })

      const { minFixedRate, minVariableRate } = lendersWithRates[0] || {}

      return Math.min(minFixedRate, minVariableRate)
    }
    return null
  }, [isScriptLoaded, availableLenders])

  const isFullTable = variation !== SHORTENED_RATE_TABLE_VARIATION
  const isOrderedRateTable = variation === ORDERED_RATE_TABLE_VARIATION

  const showDisclosure = ADVERTISER_DISCLOSURE_THEMES.includes(theme)
  const metaQuery = getMetaQuery({ ...metaParams, marketplace: verticals, type }, theme)
  const ctaUrl = getCtaUrl({ utms, theme, metaQuery })

  const commonProps = {
    ...props,
    availableLenders,
    metaQuery,
  }

  return (
    <div>
      {showDisclosure && <AdvertiserDisclosure tooltipId="fox-disclosure" theme={theme} />}
      <div className={styles.rateTableContainer}>
        <div className={styles.header}>
          <PoweredBy stylesClassName="disclaimer" className={`mb-8 ${styles.poweredByDesktop}`} />
          <div className={styles.flexWrap}>
            <div>
              <p className={styles.title}>Find the right student loan for you.</p>
              <p className={`mt-sm-8 ${styles.subHeader}`}>
                Rates from {apr(minRate)} APR.
                <Superscript callback={() => setIsDisclosureOpen(true)} /> Compare up to{' '}
                {availableLenders.length} lenders in minutes.
              </p>
            </div>
            <div className={styles.CTA}>
              <HeaderCTA ctaUrl={ctaUrl} disclaimerText="on Credible" />
            </div>
          </div>
        </div>
        {isFullTable ? (
          <FullRateTableApp {...commonProps} isOrderedRateTable={isOrderedRateTable} />
        ) : (
          <ShortenedRateTableApp {...commonProps} />
        )}
        <FooterLinks containerClassName={styles.footerLinks} small />
        <PoweredBy stylesClassName="disclaimer" className={`${styles.poweredByMobile}`} />
        <RateTableFooterDisclosure
          slug="inschool.landing.footer"
          isOpen={isDisclosureOpen}
          toggleDisclosure={() => setIsDisclosureOpen((prevOpen) => !prevOpen)}
        />
      </div>
    </div>
  )
}

RateTableApp.propTypes = {
  isScriptLoaded: PropTypes.bool.isRequired,
  utms: PropTypes.shape({
    utm_source: PropTypes.string,
    utm_campaign: PropTypes.string,
    utm_medium: PropTypes.string,
    utm_content: PropTypes.string,
    credclid: PropTypes.string,
    renderId: PropTypes.string,
  }),
  theme: PropTypes.string,
  variation: PropTypes.string,
  metaParams: PropTypes.object,
  verticals: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  type: PropTypes.string,
}

// Export named class and functions for unit tests
export { RateTableApp as RateTableAppNoHoc }

export default compose(withApolloProvider, WidgetPage, withRateScript)(RateTableApp)
